/* eslint-disable no-param-reassign */
import Framework from '@greenville/framework';
import { applySnapshot, getSnapshot, types } from 'mobx-state-tree';
import * as constants from '../../../common/constants';
import utils from '../../../common/utils';
import { buildRowScopedExportPayload } from '../common/utils/auditExportColumnUtils';

const DataItem = types.model('DataItem', {
  requestId: types.maybeNull(types.string),
  serverDateTime: types.maybeNull(types.string),
  createdDate: types.maybeNull(types.number),
  question: types.maybeNull(types.string),
  identifierId: types.maybeNull(types.string),
  graphId: types.maybeNull(types.string),
  graphVersion: types.maybeNull(types.string),
  userId: types.maybeNull(types.string),
  type: types.maybeNull(types.string),
  agentName: types.maybeNull(types.string),
  displayType: types.maybeNull(types.string),
  title: types.maybeNull(types.string),
  audioIntent: types.maybeNull(types.boolean),
  fromAgentic: types.maybeNull(types.boolean),
  sdk: types.maybeNull(types.boolean)
});

const ObserveSearchModel = types
  .model('ObserveSearchModel', {
    data: types.optional(types.array(DataItem), []),
    totalCount: types.optional(types.union(types.number, types.null), 0),
    totalPages: types.optional(types.union(types.number, types.null), 0),
    currentPage: types.optional(types.number, 0),
    pageSize: types.optional(types.number, constants.PAGINATION.LIMIT),
    isLoading: types.optional(types.boolean, false),
    hasMorePages: types.optional(types.boolean, false),
    lastPayload: types.optional(types.frozen(), {}),
    /** Date range from the last Search; kept intact because pagination narrows `lastPayload.endDate`. */
    searchDateRange: types.optional(types.frozen(), {}),
    /** True when last Search used both startDate and endDate (pagination may echo server endDate). */
    paginationUsesDateRange: types.optional(types.boolean, false),
    /** Latest API `searchedParams` (payload echo); used for endDate on next/prev only. */
    lastSearchedParams: types.optional(types.maybeNull(types.frozen()), null),
    cursorPaging: types.optional(types.boolean, false),
    cursorStack: types.optional(types.frozen(), [null]),
    nextCursor: types.optional(types.maybeNull(types.frozen()), null),
    requestedPage: types.optional(types.number, 0),
    initialSnapshot: types.optional(types.frozen(), {})
  })
  .views((self) => ({
    get observeData() {
      return self.data;
    },
    get hasData() {
      return self.data.length > 0;
    },
    get totalItems() {
      return self.totalCount;
    },
    get currentPageNumber() {
      return self.currentPage;
    },
    get totalPagesCount() {
      return self.totalPages;
    },
    get canLoadNextPage() {
      return self.hasMorePages && !self.isLoading;
    },
    get itemsPerPage() {
      return self.pageSize;
    },
    get isFirstPage() {
      return self.currentPage === 0;
    },
    get paginationInfo() {
      return {
        totalCount: self.totalCount,
        totalPages: self.totalPages,
        currentPage: self.currentPage,
        pageSize: self.pageSize
      };
    },
    /** Export filters; start/end dates come from loaded row timestamps, not search date pickers. */
    get exportPayload() {
      return buildRowScopedExportPayload(self.lastPayload, self.data);
    }
  }))
  .actions((self) => {
    const publishResponse = () => {
      Framework.getEventManager().publish(constants.GET_OBSERVE_SEARCH_RESPONSE);
    };

    const publishSearch = (payload) => {
      Framework.getEventManager().publish(constants.GET_OBSERVE_SEARCH_DATA, {
        payload,
        cursorPaging: self.cursorPaging
      });
    };

    const withServerEndDate = (payload) => {
      const serverEndDate = self.lastSearchedParams?.endDate;
      if (self.paginationUsesDateRange && serverEndDate != null && serverEndDate !== '') {
        return { ...payload, endDate: serverEndDate };
      }
      return payload;
    };

    /**
     * Resolves the cursor for a target page: forward uses the last nextCursor, backward re-reads the stack,
     * page 0 or a page-size change restarts from the first page.
     */
    const resolveCursorNavigation = (page, pageSize) => {
      if (page === 0 || pageSize !== self.pageSize) {
        return { page: 0, cursor: null, stack: [null] };
      }
      if (page > self.currentPage) {
        return { page, cursor: self.nextCursor, stack: [...self.cursorStack.slice(0, page), self.nextCursor] };
      }
      return { page, cursor: self.cursorStack[page] ?? null, stack: self.cursorStack.slice(0, page + 1) };
    };

    const fetchCursorPage = (page, pageSize) => {
      const navigation = resolveCursorNavigation(page, pageSize);
      const basePayload = withServerEndDate({ ...self.lastPayload, limit: pageSize });

      self.cursorStack = navigation.stack;
      self.requestedPage = navigation.page;
      self.setPageSize(pageSize);
      self.setLastPayload(basePayload);
      self.setLoading(true);
      publishSearch(navigation.cursor ? { ...basePayload, cursor: navigation.cursor } : basePayload);
    };

    const applyCursorPagination = (paginationData) => {
      self.nextCursor = paginationData?.nextCursor ?? null;
      self.setHasMorePages(Boolean(paginationData?.hasMorePages) && self.nextCursor != null);
      self.setTotalCount(null);
      self.setTotalPages(null);
      self.setCurrentPage(self.requestedPage);
    };

    const restoreCursorPage = () => {
      self.requestedPage = self.currentPage;
      self.cursorStack = self.cursorStack.slice(0, self.currentPage + 1);
    };

    return {
      setLoading(loading) {
        self.isLoading = loading;
      },
      setPageSize(size) {
        self.pageSize = size;
      },
      setLastPayload(payload) {
        self.lastPayload = payload;
      },
      setSearchDateRange(range) {
        self.searchDateRange = range;
      },
      setTotalCount(count) {
        self.totalCount = count;
      },
      setTotalPages(pages) {
        self.totalPages = pages;
      },
      setCurrentPage(page) {
        self.currentPage = page;
      },
      setHasMorePages(value) {
        self.hasMorePages = value;
      },

      fetchInitial(payload = {}, { cursorPaging = false } = {}) {
        // Clear server echo before reset so Search/Reset never reuse previous searchedParams dates
        self.lastSearchedParams = null;
        self.resetData();
        self.cursorPaging = cursorPaging;
        self.paginationUsesDateRange = Boolean(payload.startDate && payload.endDate);
        self.setLoading(true);

        // Server-side pagination; dates must come only from this Search (not lastSearchedParams)
        const { offset, ...searchPayload } = payload;
        const requestPayload = cursorPaging
          ? { ...searchPayload, limit: self.pageSize }
          : { ...searchPayload, offset: 0, limit: self.pageSize };
        const searchDateRange = {};
        if (payload.startDate != null && payload.startDate !== '') {
          requestPayload.startDate = payload.startDate;
          searchDateRange.startDate = payload.startDate;
        }
        if (payload.endDate != null && payload.endDate !== '') {
          requestPayload.endDate = payload.endDate;
          searchDateRange.endDate = payload.endDate;
        }

        self.setSearchDateRange(searchDateRange);
        self.setLastPayload(requestPayload);
        publishSearch(requestPayload);
      },

      fetchPage(page, limit = null) {
        const pageSize = limit || self.pageSize;
        if (self.cursorPaging) {
          fetchCursorPage(page, pageSize);
          return;
        }

        const requestPayload = withServerEndDate({
          ...self.lastPayload,
          offset: page * pageSize,
          limit: pageSize
        });

        self.setPageSize(pageSize);
        self.setLastPayload(requestPayload);
        self.setLoading(true);
        publishSearch(requestPayload);
      },

      fetch(payload) {
        // Legacy method for compatibility - redirects to fetchInitial
        self.fetchInitial(payload);
      },

      setResponse(response) {
        if (!response) {
          publishResponse();
          return;
        }

        // Extract response data, pagination, and optional searchedParams (server echo of filters)
        let responseData = [];
        let paginationData = null;
        let searchedParams = null;

        if (response.success !== undefined) {
          responseData = response.data || [];
          paginationData = response.pagination || null;
          searchedParams = response.searchedParams ?? paginationData?.searchedParams ?? null;
        } else if (Array.isArray(response)) {
          responseData = response;
        } else if (response.data) {
          responseData = response.data;
          paginationData = response.pagination || null;
          searchedParams = response.searchedParams ?? paginationData?.searchedParams ?? null;
        }

        // Compute createdDate (epoch UTC) from serverDateTime for each row
        const enrichedData = responseData.map((item) => ({
          ...item,
          createdDate: utils.toEpochUTC(item.serverDateTime)
        }));

        const hadExistingRows = self.data.length > 0;
        if (enrichedData.length === 0 && hadExistingRows) {
          if (self.cursorPaging) {
            restoreCursorPage();
          }
          self.setHasMorePages(false);
          self.setLoading(false);
          publishResponse();
          return;
        }

        // Update data - always replace (no append for pagination)
        applySnapshot(self.data, enrichedData);

        if (searchedParams && typeof searchedParams === 'object' && !Array.isArray(searchedParams)) {
          self.lastSearchedParams = { ...searchedParams };
        }

        // Update pagination info from server response
        if (self.cursorPaging) {
          applyCursorPagination(paginationData);
        } else if (paginationData) {
          if (paginationData.totalCount === null) {
            self.setTotalCount(null);
          } else {
            self.setTotalCount(paginationData.totalCount ?? 0);
          }
          if (paginationData.totalPages === null) {
            self.setTotalPages(null);
          } else {
            self.setTotalPages(paginationData.totalPages ?? 0);
          }
          if (paginationData.hasMorePages !== undefined) {
            self.setHasMorePages(Boolean(paginationData.hasMorePages));
          } else if (paginationData.totalCount === null) {
            // Unknown total: allow next unless API explicitly sent hasMorePages: false
            self.setHasMorePages(true);
          } else {
            self.setHasMorePages(false);
          }

          // Calculate 0-based page index from offset and limit (MaterialUI expects 0-based)
          const currentPageIndex =
            paginationData.offset !== undefined && paginationData.limit
              ? Math.floor(paginationData.offset / paginationData.limit)
              : 0;
          self.setCurrentPage(currentPageIndex);
        } else {
          // Fallback if no pagination data
          self.setTotalCount(responseData.length);
          self.setTotalPages(1);
          self.setHasMorePages(false);
          self.setCurrentPage(0);
        }

        self.setLoading(false);
        publishResponse();
      },

      resetData() {
        applySnapshot(self.data, []);
        self.setTotalCount(0);
        self.setTotalPages(0);
        self.setCurrentPage(0);
        self.setHasMorePages(false);
        self.setLastPayload({});
        self.setSearchDateRange({});
        self.paginationUsesDateRange = false;
        self.lastSearchedParams = null;
        self.cursorStack = [null];
        self.nextCursor = null;
        self.requestedPage = 0;
      },

      resetStoreValues() {
        // Legacy method for compatibility
        self.setLoading(false);
        self.resetData();
        publishResponse();
      },

      afterCreate() {
        self.initialSnapshot = getSnapshot(self);
      }
    };
  });

export default ObserveSearchModel;
